/**
 * Phones: a table too wide for its box is shown as one card per row, each
 * value next to its column's name (the "stacked table" pattern), instead of
 * scrolling sideways. A table that fits stays a table.
 *
 * Column names come from the table's own header row, so no table needs
 * changes. A table that must keep its columns (a grid read across, say)
 * opts out with `data-mobile-table="scroll"` on itself or an ancestor.
 *
 * This module only sets attributes; the card look is CSS under the phone
 * media query (`stackedTableStyles`), so leftovers are inert on desktop.
 */

/** Same edge as the theme's `sm` breakpoint (600 px). */
export const PHONE_QUERY = '(max-width: 599.95px)';

/** On a table shown as cards. */
export const STACK_ATTR = 'data-stacked';
/** On each body cell: its column's name (empty: no label shown). */
export const LABEL_ATTR = 'data-label';
export const OPT_OUT_SELECTOR = '[data-mobile-table="scroll"]';

const clean = (text: string | null): string =>
  (text ?? '').replace(/\s+/g, ' ').trim();

/** Column names by column index, from the last header row. */
export function columnLabels(table: HTMLTableElement): string[] {
  const head = table.tHead;
  const row = head?.rows[head.rows.length - 1];
  if (!row) return [];
  const labels: string[] = [];
  for (const cell of Array.from(row.cells)) {
    labels.push(clean(cell.textContent));
    for (let i = 1; i < cell.colSpan; i++) labels.push('');
  }
  return labels;
}

/**
 * Writes each body and footer cell's column name to `data-label`. A cell
 * spanning columns (no data, loading, expandable details) gets none.
 */
export function labelCells(
  table: HTMLTableElement,
  labels: string[] = columnLabels(table)
): void {
  const sections = [
    ...Array.from(table.tBodies),
    ...(table.tFoot ? [table.tFoot] : [])
  ];
  for (const section of sections) {
    for (const row of Array.from(section.rows)) {
      let column = 0;
      for (const cell of Array.from(row.cells)) {
        const span = Math.max(1, cell.colSpan);
        const label = span > 1 ? '' : (labels[column] ?? '');
        if (cell.getAttribute(LABEL_ATTR) !== label) {
          cell.setAttribute(LABEL_ATTR, label);
        }
        column += span;
      }
    }
  }
}

/** Laid out as a table, it is wider than the box it sits in. */
export function isTooWide(table: HTMLTableElement): boolean {
  const box = table.parentElement;
  return !!box && table.offsetWidth > box.clientWidth + 1;
}

// Why each stacked table was stacked: the viewport width and its columns.
// Measuring means laying it out as a table again, so a stacked table is only
// measured again when one of those changes, not on every new row.
let decisions = new WeakMap<HTMLTableElement, string>();

/**
 * Stacks the tables under `root` that are too wide, unstacks the rest, and
 * keeps the labels of stacked tables in step with their rows. Tables are
 * visited outer first, so a nested table is measured inside its stacked
 * parent.
 */
export function updateStackedTables(
  root: Element | Document,
  viewportWidth: number,
  tooWide: (table: HTMLTableElement) => boolean = isTooWide
): void {
  for (const table of Array.from(root.querySelectorAll('table'))) {
    if (!table.tHead || table.closest(OPT_OUT_SELECTOR)) {
      table.removeAttribute(STACK_ATTR);
      decisions.delete(table);
      continue;
    }
    const labels = columnLabels(table);
    const reason = `${viewportWidth}|${labels.join('\u0001')}`;
    if (table.hasAttribute(STACK_ATTR) && decisions.get(table) === reason) {
      labelCells(table, labels);
      continue;
    }
    table.removeAttribute(STACK_ATTR);
    if (tooWide(table)) {
      labelCells(table, labels);
      table.setAttribute(STACK_ATTR, '');
      decisions.set(table, reason);
    } else {
      decisions.delete(table);
    }
  }
}

/** Back to plain tables (leaving the phone width). */
export function clearStackedTables(root: Element | Document): void {
  for (const table of Array.from(root.querySelectorAll(`[${STACK_ATTR}]`))) {
    table.removeAttribute(STACK_ATTR);
  }
  decisions = new WeakMap();
}
